"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { getConfig, type DeploymentConfig } from "~/network/api";

const STORAGE_KEY = "dido-deployment-config";

const DEFAULT_WEBHOOK_BASE = "https://workflows.apps.henga.co/webhook/dido-dev";

const DEFAULT_BASE_URLS = {
  assets: "https://objectstorage.apps.henga.co/diaspora-dollar/static-assets/",
  deployment: "https://objectstorage.apps.henga.co/diaspora-dollar/deployment/",
  bucket: "https://objectstorage.apps.henga.co/diaspora-dollar/",
  api: DEFAULT_WEBHOOK_BASE,
};

export type { DeploymentConfig };

function normalizeConfig(data: DeploymentConfig | null): DeploymentConfig | null {
  if (!data) return null;
  const baseUrls = {
    assets: (data.baseUrls?.assets ?? DEFAULT_BASE_URLS.assets).replace(/\/?$/, "/"),
    deployment: (data.baseUrls?.deployment ?? DEFAULT_BASE_URLS.deployment).replace(/\/?$/, "/"),
    bucket: (data.baseUrls?.bucket ?? DEFAULT_BASE_URLS.bucket).replace(/\/?$/, "/"),
    api: (data.baseUrls?.api ?? data.webhookBaseUrl ?? DEFAULT_WEBHOOK_BASE).replace(/\/$/, ""),
  };
  const webhookBaseUrl = baseUrls.api;
  return { ...data, baseUrls, webhookBaseUrl };
}

interface ConfigContextValue {
  config: DeploymentConfig | null;
  /** API base URL (baseUrls.api). Always set. */
  webhookBaseUrl: string;
  /** Assets base URL (baseUrls.assets). Always set. */
  assetsBaseUrl: string;
  loading: boolean;
  error: string | null;
  refreshConfig: () => Promise<void>;
}

const ConfigContext = createContext<ConfigContextValue | null>(null);

export function ConfigProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<DeploymentConfig | null>(() =>
    normalizeConfig({ environment: "staging", baseUrls: DEFAULT_BASE_URLS, webhookBaseUrl: DEFAULT_WEBHOOK_BASE })
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshConfig = useCallback(async () => {
    if (typeof window === "undefined") return;
    try {
      setLoading(true);
      setError(null);
      sessionStorage.removeItem(STORAGE_KEY);
      const data = await getConfig();
      const normalized = normalizeConfig(data);
      setConfig(normalized);
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
    } catch (err) {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        try {
          const parsed = JSON.parse(stored) as DeploymentConfig;
          setConfig(normalizeConfig(parsed));
        } catch {
          setError(err instanceof Error ? err.message : "Failed to load config");
          setConfig(normalizeConfig({ environment: "staging", baseUrls: DEFAULT_BASE_URLS, webhookBaseUrl: DEFAULT_WEBHOOK_BASE }));
        }
      } else {
        setError(err instanceof Error ? err.message : "Failed to load config");
        setConfig(normalizeConfig({ environment: "staging", baseUrls: DEFAULT_BASE_URLS, webhookBaseUrl: DEFAULT_WEBHOOK_BASE }));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as DeploymentConfig;
        setConfig(normalizeConfig(parsed));
        setLoading(false);
        return;
      } catch {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    }
    setConfig(normalizeConfig({ environment: "staging", baseUrls: DEFAULT_BASE_URLS, webhookBaseUrl: DEFAULT_WEBHOOK_BASE }));
    refreshConfig();
  }, [refreshConfig]);

  const value = useMemo(
    () => ({
      config,
      webhookBaseUrl: (config?.baseUrls?.api ?? config?.webhookBaseUrl ?? DEFAULT_WEBHOOK_BASE).replace(/\/$/, ""),
      assetsBaseUrl: (config?.baseUrls?.assets ?? DEFAULT_BASE_URLS.assets).replace(/\/?$/, "/"),
      loading,
      error,
      refreshConfig,
    }),
    [config, loading, error, refreshConfig]
  );

  return (
    <ConfigContext.Provider value={value}>{children}</ConfigContext.Provider>
  );
}

export function useDeploymentConfig() {
  const ctx = useContext(ConfigContext);
  if (!ctx) throw new Error("useDeploymentConfig must be used within ConfigProvider");
  return ctx;
}
